// Comercial › Painel › "Mapa de prospecção" (DT FEPAM 19/2026).
// Mostra no mapa as empresas com licença ambiental perto da mancha da cheia de maio/2024, com a
// situação da licença e da renovação, para orientar a prospecção. Os dados e a prioridade vêm
// prontos do servidor (ações `pm_*`); a tela só filtra, mostra e grava status/observações.
// Regras puras: ./regras.ts. Contrato: ./api.ts e docs/mapa-prospeccao.md.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import maplibregl, { type GeoJSONSource, type Map as MapLibreMap, type MapLayerMouseEvent } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  Alert, App as AntApp, Button, Collapse, Descriptions, Drawer, Empty, Flex, Input, Segmented, Select, Spin, Switch, Table, Tag, theme,
  Tooltip, Typography, type DescriptionsProps, type TableColumnsType,
} from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { Feature, FeatureCollection, Geometry, Point } from 'geojson';
import { atualizar, camadaPartes, ehConflito, ehSemAcesso, erroDe, lerLicenca, licencaTexto, obter } from './api';
import {
  aplicarFiltros, bboxDaLista, comCoordenada, corDaSituacao, corDoRisco, filtrosPadrao, fmtCnpj, fmtData, fmtDataLocal, frasesDosNumeros,
  hojeIso, LIMITE_OBSERVACOES, ligadasPadrao, linhaCondicionantes, linhaCorpoReceptor,
  nomeDaCampanha, nomeDoModelo, opcoesDe, ordenarPorPrioridade, ORDEM_NIVEL, ORDEM_PORTE, ORDEM_POTENCIAL, ORDEM_RENOVACAO, ORDEM_SITUACAO, ORDEM_STATUS,
  raioDoNivel, resumoDaLista, resumoDosIndicios, rotulo, rotuloDoRisco, textoDistancia, textoVence, trechosDe, ZOOM_MINIMO_MANCHAS,
  type Bbox, type Camada, type Filtros, type IndicioResumo, type LicencaIndiciosCompleto, type Obter, type Prospecto, type Rotulos,
} from './regras';

const { Text } = Typography;

const LIMITE_CELULAR = 900;
const ID_PONTOS = 'prospectos';
const CAMADA_PONTOS = 'prospectos-pontos';
const CAMADA_SELECAO = 'prospectos-selecao';
const idFonteMancha = (codigo: string) => `camada-${codigo}`;
const RS_CENTRO: [number, number] = [-51.93, -30.03];
const SEM_PROSPECTOS: Prospecto[] = [];
const SEM_CAMADAS: Camada[] = [];

const COR_NIVEL: Record<string, string> = { alta: 'red', media: 'orange', baixa: 'default' };
const COR_STATUS: Record<string, string> = {
  novo: 'default', contatado: 'blue', reuniao: 'purple', proposta: 'geekblue', cliente: 'green', descartado: 'default',
};

// ── Mapa-base (molde: pages/OperacionalMapaGeral.tsx) ────────────────────────
function estiloOsm(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      osm: {
        type: 'raster', tileSize: 256,
        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        attribution: '© OpenStreetMap',
      },
    },
    layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
  };
}

function estiloSatelite(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      esri: {
        type: 'raster', tileSize: 256,
        tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
        attribution: '© Esri',
      },
    },
    layers: [{ id: 'esri', type: 'raster', source: 'esri' }],
  };
}

type Base = 'osm' | 'satelite';
const estiloDa = (b: Base) => (b === 'satelite' ? estiloSatelite() : estiloOsm());

function geojsonPontos(lista: Prospecto[]): FeatureCollection<Point> {
  return {
    type: 'FeatureCollection',
    features: comCoordenada(lista).map((p) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [p.lon as number, p.lat as number] },
      properties: {
        id: p.id,
        nome: p.razao_social,
        municipio: p.municipio ?? '',
        cor: corDaSituacao(p.situacao_mancha),
        raio: raioDoNivel(p.prioridade?.nivel),
        descartado: p.status === 'descartado',
      },
    })),
  };
}

function useCelular(limite: number): boolean {
  const [celular, setCelular] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth <= limite);
  useEffect(() => {
    const confere = () => setCelular(window.innerWidth <= limite);
    window.addEventListener('resize', confere);
    confere();
    return () => window.removeEventListener('resize', confere);
  }, [limite]);
  return celular;
}

/** Códigos de um dicionário do servidor, na ordem conhecida; o que for novo vai no fim. */
function codigosDe(rotulos: Rotulos | undefined, campo: string, ordem: string[]): string[] {
  const chaves = Object.keys(rotulos?.[campo] ?? {});
  if (!chaves.length) return ordem;
  return [...ordem.filter((o) => chaves.includes(o)), ...chaves.filter((k) => !ordem.includes(k))];
}

// ── Pecinhas de tela ─────────────────────────────────────────────────────────
function Bolinha({ cor, tamanho = 8 }: { cor: string; tamanho?: number }) {
  return (
    <span
      aria-hidden
      style={{ display: 'inline-block', width: tamanho, height: tamanho, borderRadius: '50%', background: cor, marginRight: 5, flex: '0 0 auto' }}
    />
  );
}

function ChipSituacao({ codigo, rotulos }: { codigo: string; rotulos: Rotulos }) {
  const cor = corDaSituacao(codigo);
  return (
    <Tag style={{ margin: 0, background: `${cor}1f`, borderColor: `${cor}66`, color: '#1f2937', whiteSpace: 'normal' }}>
      <Bolinha cor={cor} />
      {rotulo(rotulos, 'situacao_mancha', codigo)}
    </Tag>
  );
}

function Campo({ nome, children }: { nome: string; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>{nome}</Text>
      {children}
    </div>
  );
}

// ── O que a licença diz ──────────────────────────────────────────────────────
const LISTA_SEM_MARCADOR: React.CSSProperties = { margin: '2px 0 0', paddingLeft: 18 };

function Lista({ titulo, itens, italico }: { titulo: string; itens: string[]; italico?: boolean }) {
  if (!itens.length) return null;
  return (
    <div>
      <Text strong style={{ fontSize: 12 }}>{titulo}</Text>
      <ul style={LISTA_SEM_MARCADOR}>
        {itens.map((t, i) => <li key={`${i}-${t.slice(0, 24)}`} style={{ fontStyle: italico ? 'italic' : undefined }}>{t}</li>)}
      </ul>
    </div>
  );
}

function ChipsDe({ itens, cor }: { itens: IndicioResumo[]; cor?: string }) {
  return (
    <>
      {itens.map((i) => <Tag key={i.codigo} color={cor} style={{ marginBottom: 4 }}>{i.nome}</Tag>)}
    </>
  );
}

interface TextoDaLicenca { texto: string | null; indicios: LicencaIndiciosCompleto | null }

function SecaoLicenca({ p, podeEditar, onSalvo }: { p: Prospecto; podeEditar: boolean; onSalvo: (p: Prospecto) => void }) {
  const { token } = theme.useToken();
  const [lendo, setLendo] = useState(false);
  const [erroLeitura, setErroLeitura] = useState<string | null>(null);
  // Texto e trechos vêm numa chamada só (`pm_licenca_texto`), sob demanda, e ficam guardados para as duas portas.
  const [doTexto, setDoTexto] = useState<TextoDaLicenca | undefined>(undefined);
  const [carregandoTexto, setCarregandoTexto] = useState(false);
  const [textoAberto, setTextoAberto] = useState(false);
  const [erroTexto, setErroTexto] = useState<string | null>(null);

  const ind = p.licenca_indicios;
  const leitura = p.licenca_leitura;
  const numeros = frasesDosNumeros(ind);
  const corpoReceptor = linhaCorpoReceptor(ind);
  const condicionantes = linhaCondicionantes(ind);
  const temAssunto = resumoDosIndicios(ind).length > 0;

  const carregarTexto = async () => {
    if (doTexto !== undefined || carregandoTexto) return;
    setCarregandoTexto(true);
    setErroTexto(null);
    try {
      const r = await licencaTexto(p.id);
      setDoTexto({ texto: r.texto, indicios: r.indicios });
    } catch (e) {
      setErroTexto(erroDe(e));
      setTextoAberto(false);
    } finally {
      setCarregandoTexto(false);
    }
  };

  const verTexto = () => {
    if (textoAberto) { setTextoAberto(false); return; }
    setTextoAberto(true);
    void carregarTexto();
  };

  const ler = async () => {
    setLendo(true);
    setErroLeitura(null);
    try {
      // leitura já pronta = "Ler de novo": o servidor só refaz com `refazer`
      const r = await lerLicenca(p.id, leitura?.status === 'pronto' && !!leitura.texto);
      onSalvo(r.prospecto);
    } catch (e) {
      setErroLeitura(erroDe(e));
    } finally {
      setLendo(false);
    }
  };

  const itensComTrecho = [...(doTexto?.indicios?.estruturas ?? []), ...(doTexto?.indicios?.temas ?? [])].filter((i) => trechosDe(i).length > 0);

  const pronto = leitura?.status === 'pronto' && !!leitura.texto;
  const t = pronto ? leitura?.texto : null;

  return (
    <Flex vertical gap={10}>
      <Text strong>O que a licença diz</Text>
      {!ind ? (
        <Text type="secondary">O sistema ainda não leu esta licença.</Text>
      ) : (
        <>
          {!temAssunto
            ? <Text type="secondary">Nada sobre cheia, drenagem ou estrutura de risco foi encontrado no texto.</Text>
            : (
              <div>
                <ChipsDe itens={ind.estruturas} cor="geekblue" />
                <ChipsDe itens={ind.temas} cor="cyan" />
              </div>
            )}
          {numeros.length ? <Text>{numeros.join(' · ')}</Text> : null}
          {corpoReceptor ? <Text>{corpoReceptor}</Text> : null}
          {condicionantes ? <Text type="secondary" style={{ fontSize: 12 }}>{condicionantes}</Text> : null}
          {temAssunto ? (
            <Collapse
              size="small"
              onChange={(chaves) => { if (chaves.length) void carregarTexto(); }}
              items={[{
                key: 'trechos',
                label: 'Trechos da licença',
                children: carregandoTexto ? (
                  <Flex justify="center"><Spin size="small" /></Flex>
                ) : doTexto === undefined ? (
                  <Text type="secondary">Não deu para carregar os trechos agora.</Text>
                ) : !doTexto.indicios ? (
                  <Text type="secondary">O sistema ainda não leu esta licença.</Text>
                ) : itensComTrecho.length === 0 ? (
                  <Text type="secondary">Sem trechos para mostrar.</Text>
                ) : (
                  <Flex vertical gap={10}>
                    {itensComTrecho.map((i) => (
                      <div key={i.codigo}>
                        <Text strong style={{ fontSize: 12 }}>{i.nome}</Text>
                        {trechosDe(i).map((tr, k) => (
                          <div key={`${k}-${tr.slice(0, 24)}`} style={{ fontStyle: 'italic', fontSize: 13, marginTop: 2 }}>“{tr}”</div>
                        ))}
                      </div>
                    ))}
                  </Flex>
                ),
              }]}
            />
          ) : null}
        </>
      )}

      <Flex gap={8} wrap align="center">
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={verTexto} loading={carregandoTexto && textoAberto}>
          {textoAberto ? 'Esconder o texto da licença' : 'Ver o texto da licença'}
        </Button>
        {podeEditar ? (
          <Tooltip title={ind ? undefined : 'O sistema ainda não leu o texto desta licença.'}>
            <span>
              <Button type={pronto ? 'default' : 'primary'} onClick={ler} loading={lendo} disabled={!ind}>
                {pronto ? 'Ler de novo' : 'Ler a licença com IA'}
              </Button>
            </span>
          </Tooltip>
        ) : null}
      </Flex>
      {erroTexto ? <Alert type="error" showIcon title={erroTexto} /> : null}
      {textoAberto && !carregandoTexto && doTexto !== undefined ? (
        doTexto.texto ? (
          <pre
            style={{
              margin: 0, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12,
              padding: 10, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: token.borderRadius,
              background: token.colorFillQuaternary, fontFamily: 'inherit',
            }}
          >
            {doTexto.texto}
          </pre>
        ) : <Text type="secondary">O sistema ainda não leu esta licença.</Text>
      ) : null}
      {lendo ? <Text type="secondary" style={{ fontSize: 12 }}>Lendo a licença… isto pode levar até 20 segundos.</Text> : null}
      {erroLeitura ? <Alert type="error" showIcon title={erroLeitura} /> : null}
      {leitura?.status === 'erro' && !lendo ? (
        <Alert type="warning" showIcon title={leitura.erro || 'A leitura da licença não deu certo. Tente de novo.'} />
      ) : null}
      {pronto && leitura?.ultimo_erro && !lendo ? (
        <Alert type="warning" showIcon title={`A última tentativa de ler de novo falhou: ${leitura.ultimo_erro} A leitura abaixo é a anterior.`} />
      ) : null}

      {t ? (
        <Flex
          vertical gap={10}
          style={{ padding: 12, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: token.borderRadius, background: token.colorFillQuaternary }}
        >
          <div><Tag color={corDoRisco(t.risco_diretriz)} style={{ margin: 0 }}>Risco pela diretriz: {rotuloDoRisco(t.risco_diretriz)}</Tag></div>
          <Lista titulo="Por quê" itens={t.por_que ?? []} />
          {(t.estruturas ?? []).length ? (
            <div>
              <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>Estruturas</Text>
              {t.estruturas.map((e) => <Tag key={e} style={{ marginBottom: 4 }}>{e}</Tag>)}
            </div>
          ) : null}
          <Lista titulo="O que o órgão vai pedir" itens={t.o_que_o_orgao_vai_pedir ?? []} />
          <Lista titulo="Condicionantes relevantes" itens={t.condicionantes_relevantes ?? []} italico />
          {t.gancho ? (
            <div style={{ borderLeft: `3px solid ${token.colorPrimary}`, paddingLeft: 10 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Gancho para a conversa</Text>
              <Text strong>{t.gancho}</Text>
            </div>
          ) : null}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Lida em {fmtDataLocal(leitura?.lido_em)}
            {leitura?.lido_por ? ` por ${leitura.lido_por}` : ''}
            {leitura?.modelo ? ` (${nomeDoModelo(leitura.modelo)})` : ''}.
          </Text>
        </Flex>
      ) : null}
    </Flex>
  );
}

// ── Gaveta do prospecto ──────────────────────────────────────────────────────
interface GavetaProps {
  p: Prospecto;
  rotulos: Rotulos;
  podeEditar: boolean;
  hoje: string;
  onSalvo: (p: Prospecto) => void;
  onConflito: () => void;
  onCentralizar: () => void;
}

function Gaveta({ p, rotulos, podeEditar, hoje, onSalvo, onConflito, onCentralizar }: GavetaProps) {
  const { message } = AntApp.useApp();
  const [status, setStatus] = useState<string>(p.status);
  const [obs, setObs] = useState<string>(p.observacoes ?? '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const mudouStatus = status !== p.status;
  const mudouObs = obs !== (p.observacoes ?? '');
  const temCoordenada = p.lat != null && p.lon != null;

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      const r = await atualizar({
        id: p.id,
        ...(mudouStatus ? { status } : {}),
        ...(mudouObs ? { observacoes: obs } : {}),
        visto: { atualizado_em: p.atualizado_em },
      });
      message.success('Salvo.');
      onSalvo(r.prospecto);
    } catch (e) {
      if (ehConflito(e)) {
        message.warning(erroDe(e));
        onConflito();
      } else {
        setErro(erroDe(e));
      }
    } finally {
      setSalvando(false);
    }
  };

  const opcoesStatus = codigosDe(rotulos, 'status', ORDEM_STATUS).map((c) => ({ value: c, label: rotulo(rotulos, 'status', c) }));
  const mostraDistancia = p.situacao_mancha !== 'dentro' && p.distancia_mancha_m != null && p.situacao_mancha !== 'sem_coordenada';

  const itens: DescriptionsProps['items'] = [
    { key: 'cnpj', label: 'CNPJ', children: p.cnpj_fmt || fmtCnpj(p.cnpj) },
    { key: 'local', label: 'Local', children: [p.municipio, p.endereco].filter(Boolean).join(' — ') || '—' },
    {
      key: 'atividade', label: 'Atividade',
      children: (
        <>
          <div>{p.atividade || '—'}</div>
          {p.codram ? <Text type="secondary" style={{ fontSize: 12 }}>{p.codram}</Text> : null}
        </>
      ),
    },
    { key: 'porte', label: 'Porte', children: p.porte || '—' },
    { key: 'potencial', label: 'Potencial poluidor', children: p.potencial_poluidor || '—' },
    {
      key: 'licenca', label: 'Licença',
      children: (
        <>
          <div>{p.licenca || '—'}</div>
          {p.situacao_licenca ? <Tag style={{ marginTop: 4 }}>{p.situacao_licenca}</Tag> : null}
        </>
      ),
    },
    {
      key: 'vigencia', label: 'Fim da vigência',
      children: <>{fmtData(p.fim_vigencia)}{p.fim_vigencia ? <Text type="secondary"> ({textoVence(p.fim_vigencia, hoje)})</Text> : null}</>,
    },
    { key: 'pedir', label: 'Pedir renovação até', children: fmtData(p.renovacao_ate) },
    { key: 'renov', label: 'Renovação', children: rotulo(rotulos, 'renovacao_situacao', p.renovacao_situacao) },
    { key: 'processo', label: 'Processo de renovação', children: p.processo_renovacao || 'Nenhum processo aberto' },
    {
      key: 'mancha', label: 'Na mancha',
      children: (
        <>
          <ChipSituacao codigo={p.situacao_mancha} rotulos={rotulos} />
          {mostraDistancia ? <Text type="secondary" style={{ marginLeft: 8 }}>{textoDistancia(p.distancia_mancha_m)}</Text> : null}
        </>
      ),
    },
    {
      key: 'prioridade', label: 'Prioridade',
      children: (
        <>
          <Tag color={COR_NIVEL[p.prioridade?.nivel] ?? 'default'}>{rotulo(rotulos, 'nivel', p.prioridade?.nivel)}</Tag>
          {(p.prioridade?.motivos ?? []).map((m) => <Tag key={m} style={{ marginBottom: 4 }}>{m}</Tag>)}
        </>
      ),
    },
    {
      key: 'cliente', label: 'Cliente',
      children: p.cliente_id
        ? <Link to={`/comercial/clientes?cliente=${p.cliente_id}`}>{p.cliente_nome || 'Abrir o cadastro do cliente'}</Link>
        : 'Ainda não é cliente',
    },
  ];

  return (
    <Flex vertical gap={16}>
      <Descriptions size="small" bordered column={1} items={itens} styles={{ label: { width: 150 } }} />

      <SecaoLicenca p={p} podeEditar={podeEditar} onSalvo={onSalvo} />

      <Flex gap={8} wrap>
        <Button href={p.ficha_url ?? undefined} target="_blank" rel="noopener noreferrer" disabled={!p.ficha_url}>Ficha na FEPAM</Button>
        <Button href={p.pdf_url ?? undefined} target="_blank" rel="noopener noreferrer" disabled={!p.pdf_url}>PDF da licença</Button>
        <Button onClick={onCentralizar} disabled={!temCoordenada}>Centralizar no mapa</Button>
      </Flex>
      {!temCoordenada ? <Text type="secondary" style={{ fontSize: 12 }}>Esta empresa não tem coordenada, por isso não aparece no mapa.</Text> : null}

      <div>
        <Text strong>Andamento da prospecção</Text>
        {podeEditar ? (
          <Flex vertical gap={10} style={{ marginTop: 8 }}>
            <Campo nome="Situação">
              <Select
                value={status} onChange={setStatus} options={opcoesStatus} style={{ width: '100%' }}
                showSearch optionFilterProp="label" disabled={salvando}
              />
            </Campo>
            <Campo nome="Observações">
              <Input.TextArea
                value={obs} onChange={(e) => setObs(e.target.value)} rows={4} maxLength={LIMITE_OBSERVACOES} showCount
                disabled={salvando} placeholder="Quem foi contatado, o que foi dito, próximo passo…"
              />
            </Campo>
            {erro ? <Alert type="error" showIcon title={erro} /> : null}
            <div>
              <Button type="primary" onClick={salvar} loading={salvando} disabled={!mudouStatus && !mudouObs}>Salvar</Button>
            </div>
          </Flex>
        ) : (
          <Flex vertical gap={6} style={{ marginTop: 8 }}>
            <div><Tag color={COR_STATUS[p.status] ?? 'default'}>{rotulo(rotulos, 'status', p.status)}</Tag></div>
            <Text>{p.observacoes || 'Sem observações.'}</Text>
          </Flex>
        )}
        {p.status_em ? (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            Situação mudada em {fmtData(p.status_em)}{p.status_por ? ` por ${p.status_por}` : ''}.
          </Text>
        ) : null}
      </div>
    </Flex>
  );
}

// ── A página ─────────────────────────────────────────────────────────────────
export default function MapaProspeccao() {
  const { message } = AntApp.useApp();
  const { token } = theme.useToken();
  const celular = useCelular(LIMITE_CELULAR);

  const [dados, setDados] = useState<Obter | null>(null);
  const [erro, setErro] = useState<{ texto: string; semAcesso: boolean } | null>(null);
  const [lendo, setLendo] = useState(true);
  const [filtros, setFiltros] = useState<Filtros>(filtrosPadrao);
  const [pagina, setPagina] = useState(1);
  const [ligadas, setLigadas] = useState<Record<string, boolean>>({});
  const [selecionadoId, setSelecionadoId] = useState<number | null>(null);
  const [base, setBase] = useState<Base>('osm');
  const [painelAberto, setPainelAberto] = useState(true);
  const [legendaAberta, setLegendaAberta] = useState(true);
  const [mapaPronto, setMapaPronto] = useState(false);
  const [zoomOk, setZoomOk] = useState(false);
  const [carregandoManchas, setCarregandoManchas] = useState(false);
  const [erroManchas, setErroManchas] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const baseAplicadaRef = useRef<Base>('osm');
  const cacheRef = useRef<Map<string, Map<string, Feature<Geometry>>>>(new Map());
  const emCargaRef = useRef<Set<string>>(new Set());
  const repetirRef = useRef<Set<string>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Dados ──────────────────────────────────────────────────────────────────
  const carregar = useCallback(async (campanha?: string) => {
    setLendo(true);
    try {
      const d = await obter(campanha);
      setDados(d);
      setErro(null);
      setLigadas((prev) => {
        const novo = { ...prev };
        const padrao = ligadasPadrao(d.camadas);
        for (const c of d.camadas) if (!(c.codigo in novo)) novo[c.codigo] = padrao[c.codigo];
        return novo;
      });
    } catch (e) {
      setErro({ texto: erroDe(e), semAcesso: ehSemAcesso(e) });
    } finally {
      setLendo(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const prospectos = dados?.prospectos ?? SEM_PROSPECTOS;
  const rotulos = useMemo<Rotulos>(() => dados?.rotulos ?? {}, [dados]);
  const camadas = useMemo(() => (dados ? [...dados.camadas].sort((a, b) => a.ordem - b.ordem) : SEM_CAMADAS), [dados]);
  const filtrados = useMemo(() => aplicarFiltros(prospectos, filtros), [prospectos, filtros]);
  const lista = useMemo(() => ordenarPorPrioridade(filtrados), [filtrados]);
  const resumo = useMemo(() => resumoDaLista(filtrados), [filtrados]);
  const hoje = useMemo(() => hojeIso(), []);
  const selecionado = useMemo(() => prospectos.find((x) => x.id === selecionadoId) ?? null, [prospectos, selecionadoId]);
  const semCampanha = !!dados && (!dados.campanha || dados.campanhas.length === 0);
  const temMapa = !!dados && !semCampanha;

  // Últimos valores para os eventos do mapa (que são registrados uma vez só).
  const filtradosRef = useRef(filtrados);
  const camadasRef = useRef(camadas);
  const ligadasRef = useRef(ligadas);
  const selecionadoIdRef = useRef(selecionadoId);
  filtradosRef.current = filtrados;
  camadasRef.current = camadas;
  ligadasRef.current = ligadas;
  selecionadoIdRef.current = selecionadoId;

  // ── Mapa: montagem de fontes e camadas (idempotente) ───────────────────────
  const colecaoDaMancha = useCallback((codigo: string): FeatureCollection<Geometry> => ({
    type: 'FeatureCollection',
    features: Array.from(cacheRef.current.get(codigo)?.values() ?? []),
  }), []);

  const sincronizar = useCallback((map: MapLibreMap) => {
    try {
      if (!map.getSource(ID_PONTOS)) map.addSource(ID_PONTOS, { type: 'geojson', data: geojsonPontos(filtradosRef.current) });
      if (!map.getLayer(CAMADA_PONTOS)) {
        map.addLayer({
          id: CAMADA_PONTOS, type: 'circle', source: ID_PONTOS,
          layout: { 'circle-sort-key': ['get', 'raio'] },
          paint: {
            'circle-color': ['get', 'cor'],
            'circle-radius': ['get', 'raio'],
            'circle-opacity': ['case', ['get', 'descartado'], 0.35, 1],
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1.5,
            'circle-stroke-opacity': ['case', ['get', 'descartado'], 0.5, 1],
          },
        });
      }
      if (!map.getLayer(CAMADA_SELECAO)) {
        map.addLayer({
          id: CAMADA_SELECAO, type: 'circle', source: ID_PONTOS,
          filter: ['==', ['get', 'id'], selecionadoIdRef.current ?? -1],
          paint: {
            'circle-color': 'rgba(0,0,0,0)',
            'circle-radius': ['+', ['get', 'raio'], 5],
            'circle-stroke-color': '#0930D0',
            'circle-stroke-width': 3,
          },
        });
      }
      for (const c of camadasRef.current) {
        const fonte = idFonteMancha(c.codigo);
        if (!map.getSource(fonte)) map.addSource(fonte, { type: 'geojson', data: colecaoDaMancha(c.codigo) });
        const visibilidade = ligadasRef.current[c.codigo] === false ? 'none' : 'visible';
        if (!map.getLayer(`${fonte}-fill`)) {
          map.addLayer({
            id: `${fonte}-fill`, type: 'fill', source: fonte, layout: { visibility: visibilidade },
            paint: { 'fill-color': c.cor, 'fill-opacity': c.opacidade },
          }, CAMADA_PONTOS);
        }
        if (!map.getLayer(`${fonte}-line`)) {
          map.addLayer({
            id: `${fonte}-line`, type: 'line', source: fonte, layout: { visibility: visibilidade },
            paint: { 'line-color': c.cor, 'line-width': 1 },
          }, CAMADA_PONTOS);
        }
        map.setLayoutProperty(`${fonte}-fill`, 'visibility', visibilidade);
        map.setLayoutProperty(`${fonte}-line`, 'visibility', visibilidade);
      }
    } catch {
      // Estilo ainda carregando: tenta de novo quando o mapa ficar parado.
      map.once('idle', () => sincronizar(map));
    }
  }, [colecaoDaMancha]);

  // ── Mapa: manchas por retângulo da tela ────────────────────────────────────
  const carregarManchas = useCallback(async (codigo: string) => {
    const map = mapRef.current;
    if (!map || map.getZoom() < ZOOM_MINIMO_MANCHAS) return;
    if (emCargaRef.current.has(codigo)) { repetirRef.current.add(codigo); return; }
    emCargaRef.current.add(codigo);
    setCarregandoManchas(true);
    try {
      do {
        repetirRef.current.delete(codigo);
        const b = map.getBounds();
        const bbox: Bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
        let cache = cacheRef.current.get(codigo);
        if (!cache) { cache = new Map(); cacheRef.current.set(codigo, cache); }
        for (;;) {
          const r = await camadaPartes(codigo, bbox, Array.from(cache.keys()));
          if (mapRef.current !== map) return;
          for (const parte of r.partes) {
            const f = parte.geojson as unknown as Feature<Geometry>;
            cache.set(parte.chave, { ...f, properties: { ...(f.properties ?? {}), chave: parte.chave, nome: parte.nome } });
          }
          (map.getSource(idFonteMancha(codigo)) as GeoJSONSource | undefined)?.setData(colecaoDaMancha(codigo));
          if (!(r.restantes > 0) || r.partes.length === 0) break;
        }
        setErroManchas(null);
      } while (repetirRef.current.has(codigo) && ligadasRef.current[codigo] !== false);
    } catch (e) {
      setErroManchas(erroDe(e));
    } finally {
      emCargaRef.current.delete(codigo);
      setCarregandoManchas(emCargaRef.current.size > 0);
    }
  }, [colecaoDaMancha]);

  const carregarTodasAsManchas = useCallback(() => {
    for (const c of camadasRef.current) if (ligadasRef.current[c.codigo] !== false) void carregarManchas(c.codigo);
  }, [carregarManchas]);

  // ── Mapa: criação (uma vez, quando a página tem o que mostrar) ─────────────
  useEffect(() => {
    if (!temMapa || !containerRef.current || mapRef.current) return undefined;
    const alvo = bboxDaLista(filtradosRef.current);
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: estiloDa(baseAplicadaRef.current),
      ...(alvo
        ? { bounds: [[alvo[0], alvo[1]], [alvo[2], alvo[3]]] as [[number, number], [number, number]], fitBoundsOptions: { padding: 40, maxZoom: 12 } }
        : { center: RS_CENTRO, zoom: 6 }),
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-right');
    mapRef.current = map;

    const popup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });
    let ultimoId: number | null = null;
    const aoPassar = (e: MapLayerMouseEvent) => {
      const f = e.features?.[0];
      if (!f) return;
      map.getCanvas().style.cursor = 'pointer';
      const id = Number(f.properties?.id);
      if (id === ultimoId) return;
      ultimoId = id;
      const caixa = document.createElement('div');
      const nome = document.createElement('strong');
      nome.textContent = String(f.properties?.nome ?? '');
      const lugar = document.createElement('div');
      lugar.textContent = String(f.properties?.municipio ?? '');
      caixa.append(nome, lugar);
      popup.setLngLat((f.geometry as Point).coordinates as [number, number]).setDOMContent(caixa).addTo(map);
    };
    const aoSair = () => {
      ultimoId = null;
      map.getCanvas().style.cursor = '';
      popup.remove();
    };
    const aoClicar = (e: MapLayerMouseEvent) => {
      const id = Number(e.features?.[0]?.properties?.id);
      if (Number.isFinite(id)) setSelecionadoId(id);
    };
    map.on('mousemove', CAMADA_PONTOS, aoPassar);
    map.on('mouseleave', CAMADA_PONTOS, aoSair);
    map.on('click', CAMADA_PONTOS, aoClicar);

    map.on('moveend', () => {
      setZoomOk(map.getZoom() >= ZOOM_MINIMO_MANCHAS);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(carregarTodasAsManchas, 400);
    });
    map.on('load', () => {
      sincronizar(map);
      setZoomOk(map.getZoom() >= ZOOM_MINIMO_MANCHAS);
      setMapaPronto(true);
    });

    // Os dois conjuntos nunca são trocados; só esvaziados na saída.
    const emCarga = emCargaRef.current;
    const repetir = repetirRef.current;
    const observador = new ResizeObserver(() => map.resize());
    observador.observe(containerRef.current);

    return () => {
      observador.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      popup.remove();
      map.remove();
      mapRef.current = null;
      emCarga.clear();
      repetir.clear();
      setMapaPronto(false);
    };
  }, [temMapa, sincronizar, carregarTodasAsManchas]);

  // Troca do mapa-base: o estilo novo apaga as nossas camadas, então monta de novo.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapaPronto || baseAplicadaRef.current === base) return;
    baseAplicadaRef.current = base;
    map.setStyle(estiloDa(base), { diff: false });
    map.once('styledata', () => sincronizar(map));
  }, [base, mapaPronto, sincronizar]);

  // Camadas novas, ligadas ou desligadas: mostra/esconde e busca as manchas do que está na tela.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapaPronto) return;
    sincronizar(map);
    carregarTodasAsManchas();
  }, [mapaPronto, camadas, ligadas, sincronizar, carregarTodasAsManchas]);

  // Pontos acompanham o filtro.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapaPronto) return;
    (map.getSource(ID_PONTOS) as GeoJSONSource | undefined)?.setData(geojsonPontos(filtrados));
  }, [filtrados, mapaPronto]);

  // Anel azul no ponto aberto na gaveta.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapaPronto || !map.getLayer(CAMADA_SELECAO)) return;
    map.setFilter(CAMADA_SELECAO, ['==', ['get', 'id'], selecionadoId ?? -1]);
  }, [selecionadoId, mapaPronto]);

  // ── Ações ──────────────────────────────────────────────────────────────────
  const mudar = (parcial: Partial<Filtros>) => {
    setFiltros((f) => ({ ...f, ...parcial }));
    setPagina(1);
  };

  const voarPara = useCallback((p: Prospecto) => {
    const map = mapRef.current;
    if (!map || p.lat == null || p.lon == null) return;
    map.flyTo({ center: [p.lon, p.lat], zoom: Math.max(map.getZoom(), 13), essential: true });
  }, []);

  const abrir = (p: Prospecto) => {
    setSelecionadoId(p.id);
    voarPara(p);
    if (celular) setPainelAberto(false);
  };

  const ajustarAoFiltro = () => {
    const map = mapRef.current;
    const b = bboxDaLista(filtrados);
    if (!map) return;
    if (!b) { message.info('Nenhuma empresa com coordenada neste filtro.'); return; }
    map.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 50, maxZoom: 14, duration: 600 });
  };

  const aoSalvar = (novo: Prospecto) => {
    setDados((d) => (d ? { ...d, prospectos: d.prospectos.map((x) => (x.id === novo.id ? novo : x)) } : d));
  };

  // ── Estados da página ──────────────────────────────────────────────────────
  if (!dados) {
    if (erro) {
      return (
        <div style={{ padding: 16 }}>
          {erro.semAcesso
            ? <Alert type="warning" showIcon title="Você não tem acesso a esta página." />
            : (
              <Alert
                type="error" showIcon title={erro.texto}
                action={<Button size="small" onClick={() => carregar()}>Tentar de novo</Button>}
              />
            )}
        </div>
      );
    }
    return <Flex justify="center" style={{ padding: 48 }}><Spin size="large" /></Flex>;
  }
  if (semCampanha) {
    return (
      <div style={{ padding: 16 }}>
        <Empty description="Nenhuma campanha importada ainda. A importação é feita pela sessão de IA com a planilha da FEPAM." />
      </div>
    );
  }

  const pode = dados.pode?.editar === true;
  // Só fica cortada se a carga por páginas parou antes do fim (ou o servidor não mandou o total).
  const cortada = dados.restantes > 0 || dados.total > dados.prospectos.length;

  const opcoesCodigo = (campo: string, ordem: string[]) =>
    codigosDe(rotulos, campo, ordem).map((c) => ({ value: c, label: rotulo(rotulos, campo, c) }));
  const opcoesTexto = (campo: 'municipio' | 'potencial_poluidor' | 'porte', ordem: string[] = []) =>
    opcoesDe(prospectos, campo, ordem).map((v) => ({ value: v, label: v }));
  const seletor = (
    valor: string[], aoMudar: (v: string[]) => void, opcoes: { value: string; label: string }[], marcador: string,
  ) => (
    <Select
      mode="multiple" allowClear showSearch optionFilterProp="label" maxTagCount="responsive"
      style={{ width: '100%' }} placeholder={marcador} value={valor} onChange={aoMudar} options={opcoes}
    />
  );

  const colunas: TableColumnsType<Prospecto> = [
    {
      title: 'Empresa', key: 'empresa', width: 190,
      render: (_: unknown, p) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.razao_social}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{p.municipio ?? '—'}</Text>
        </div>
      ),
    },
    {
      title: 'Na mancha', key: 'mancha', width: 130,
      render: (_: unknown, p) => <ChipSituacao codigo={p.situacao_mancha} rotulos={rotulos} />,
    },
    {
      title: 'Nível', key: 'nivel', width: 70,
      render: (_: unknown, p) => <Tag color={COR_NIVEL[p.prioridade?.nivel] ?? 'default'} style={{ margin: 0 }}>{rotulo(rotulos, 'nivel', p.prioridade?.nivel)}</Tag>,
    },
    {
      title: 'Vigência', key: 'vigencia', width: 120,
      sorter: (a, b) => (a.fim_vigencia ?? '').localeCompare(b.fim_vigencia ?? ''),
      render: (_: unknown, p) => (
        <Tooltip title={fmtData(p.fim_vigencia)}><span style={{ fontSize: 12 }}>{textoVence(p.fim_vigencia, hoje)}</span></Tooltip>
      ),
    },
    {
      title: 'Status', key: 'status', width: 100,
      render: (_: unknown, p) => <Tag color={COR_STATUS[p.status] ?? 'default'} style={{ margin: 0 }}>{rotulo(rotulos, 'status', p.status)}</Tag>,
    },
  ];

  const blocoResumo = (
    <Flex gap={4} wrap="nowrap" style={{ overflow: 'hidden' }} aria-label="Resumo do filtro">
      <Tag style={{ margin: 0 }}>{resumo.total} empresas</Tag>
      <Tag color="red" style={{ margin: 0 }}>{resumo.dentro} dentro</Tag>
      <Tag color="orange" style={{ margin: 0 }}>{resumo.ate_200m} até 200 m</Tag>
      <Tag color="volcano" style={{ margin: 0 }}>{resumo.alta} alta</Tag>
    </Flex>
  );

  const blocoFiltros = (
    <Flex vertical gap={8}>
      <Input
        allowClear placeholder="Buscar por nome, CNPJ ou atividade" value={filtros.busca}
        onChange={(e) => mudar({ busca: e.target.value })}
      />
      <Campo nome="Município">
        {seletor(filtros.municipios, (v) => mudar({ municipios: v }), opcoesTexto('municipio'), 'Todos os municípios')}
      </Campo>
      <Campo nome="Situação na mancha">
        {seletor(filtros.situacoes, (v) => mudar({ situacoes: v }), opcoesCodigo('situacao_mancha', ORDEM_SITUACAO), 'Todas')}
      </Campo>
      <Campo nome="Andamento da prospecção">
        {seletor(filtros.status, (v) => mudar({ status: v }), opcoesCodigo('status', ORDEM_STATUS), 'Todos')}
      </Campo>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Campo nome="Prioridade">
          {seletor(filtros.niveis, (v) => mudar({ niveis: v }), opcoesCodigo('nivel', ORDEM_NIVEL), 'Todas')}
        </Campo>
        <Campo nome="Potencial poluidor">
          {seletor(filtros.potenciais, (v) => mudar({ potenciais: v }), opcoesTexto('potencial_poluidor', ORDEM_POTENCIAL), 'Todos')}
        </Campo>
        <Campo nome="Porte">
          {seletor(filtros.portes, (v) => mudar({ portes: v }), opcoesTexto('porte', ORDEM_PORTE), 'Todos')}
        </Campo>
        <Campo nome="Renovação da licença">
          {seletor(filtros.renovacoes, (v) => mudar({ renovacoes: v }), opcoesCodigo('renovacao_situacao', ORDEM_RENOVACAO), 'Todas')}
        </Campo>
      </div>
      <Flex gap={8} wrap>
        <Tooltip title="Volta ao padrão: dentro da mancha e até 200 m, sem os descartados.">
          <Button size="small" onClick={() => { setFiltros(filtrosPadrao()); setPagina(1); }}>Limpar</Button>
        </Tooltip>
        <Button size="small" onClick={ajustarAoFiltro}>Ajustar o mapa ao filtro</Button>
      </Flex>
    </Flex>
  );

  const blocoCamadas = (
    <Flex vertical gap={6}>
      <Text strong style={{ fontSize: 13 }}>Manchas no mapa</Text>
      {camadas.length === 0 ? <Text type="secondary" style={{ fontSize: 12 }}>Nenhuma mancha cadastrada ainda.</Text> : null}
      {camadas.map((c) => (
        <Flex key={c.codigo} align="center" gap={8}>
          <Switch
            size="small" checked={ligadas[c.codigo] !== false} aria-label={`Mostrar ${c.nome}`}
            onChange={(v) => setLigadas((prev) => ({ ...prev, [c.codigo]: v }))}
          />
          <span
            aria-hidden
            style={{ width: 14, height: 14, flex: '0 0 auto', background: c.cor, opacity: Math.max(c.opacidade, 0.35), border: `1px solid ${c.cor}` }}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={c.descricao || undefined}><div style={{ fontSize: 12, lineHeight: 1.25 }}>{c.nome}</div></Tooltip>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {c.versao ? `versão ${c.versao}` : null}
              {c.versao && c.url_fonte ? ' · ' : null}
              {c.url_fonte ? <a href={c.url_fonte} target="_blank" rel="noopener noreferrer">fonte</a> : null}
            </Text>
          </div>
        </Flex>
      ))}
    </Flex>
  );

  const blocoLista = (
    <Table<Prospecto>
      size="small" rowKey="id" columns={colunas} dataSource={lista} scroll={{ x: 520 }}
      pagination={{ pageSize: 50, current: pagina, onChange: setPagina, showSizeChanger: false, hideOnSinglePage: true, size: 'small' }}
      locale={{ emptyText: 'Nenhuma empresa com estes filtros.' }}
      onRow={(p) => ({
        onClick: () => abrir(p),
        style: { cursor: 'pointer', background: p.id === selecionadoId ? 'rgba(9, 48, 208, 0.08)' : undefined },
      })}
    />
  );

  const painel = (
    <div
      style={{
        flex: celular ? '0 0 auto' : '0 0 380px', width: celular ? '100%' : 380, minWidth: 0,
        maxHeight: celular ? '60vh' : undefined, overflowY: 'auto',
        background: token.colorBgContainer, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, padding: 12,
      }}
    >
      <Flex vertical gap={12}>
        <Flex align="center" justify="space-between" gap={8}>
          <div style={{ minWidth: 0, flex: 1 }}>{blocoResumo}</div>
          {celular ? (
            <Button
              size="small" icon={painelAberto ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setPainelAberto((v) => !v)} aria-expanded={painelAberto}
            >
              {painelAberto ? 'Recolher' : 'Filtros e lista'}
            </Button>
          ) : null}
        </Flex>
        {!celular || painelAberto ? (
          <>
            {dados.campanhas.length > 1 ? (
              <Campo nome="Campanha">
                <Select
                  style={{ width: '100%' }} showSearch optionFilterProp="label" value={dados.campanha ?? undefined}
                  options={dados.campanhas.map((c) => ({ value: c.codigo, label: nomeDaCampanha(c) }))}
                  onChange={(v) => { setSelecionadoId(null); setPagina(1); carregar(v); }}
                />
              </Campo>
            ) : null}
            {cortada ? <Alert type="info" showIcon title={`A lista traz só ${dados.prospectos.length} de ${dados.total} empresas. Recarregue a página.`} /> : null}
            {blocoFiltros}
            {blocoCamadas}
            {blocoLista}
          </>
        ) : null}
      </Flex>
    </div>
  );

  const legenda = (
    <div
      style={{
        position: 'absolute', left: 10, bottom: 28, zIndex: 2, maxWidth: celular ? 220 : 280, fontSize: 12,
        background: token.colorBgContainer, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 6, padding: '6px 8px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      }}
    >
      <Flex justify="space-between" align="center" gap={8}>
        <Text strong style={{ fontSize: 12 }}>Legenda</Text>
        <Button type="text" size="small" onClick={() => setLegendaAberta((v) => !v)} aria-expanded={legendaAberta}>
          {legendaAberta ? 'Esconder' : 'Mostrar'}
        </Button>
      </Flex>
      {legendaAberta ? (
        <Flex vertical gap={2} style={{ marginTop: 2 }}>
          {(['dentro', 'ate_200m', 'ate_1km', 'mais_1km'] as const).map((c) => (
            <Flex key={c} align="center"><Bolinha cor={corDaSituacao(c)} tamanho={10} />{rotulo(rotulos, 'situacao_mancha', c)}</Flex>
          ))}
          <Text type="secondary" style={{ fontSize: 11 }}>Quanto maior o círculo, maior a prioridade. Descartadas ficam esmaecidas.</Text>
          {camadas.map((c) => (
            <Flex key={c.codigo} align="center" style={{ opacity: ligadas[c.codigo] === false ? 0.45 : 1 }}>
              <span aria-hidden style={{ width: 10, height: 10, marginRight: 5, flex: '0 0 auto', background: c.cor, opacity: Math.max(c.opacidade, 0.35), border: `1px solid ${c.cor}` }} />
              {c.nome}
            </Flex>
          ))}
          {camadas.length > 0 && !zoomOk ? <Text type="warning" style={{ fontSize: 11 }}>Aproxime o mapa para ver as manchas.</Text> : null}
          {carregandoManchas ? <Text type="secondary" style={{ fontSize: 11 }}>Carregando as manchas…</Text> : null}
          {erroManchas ? <Text type="danger" style={{ fontSize: 11 }}>Não deu para carregar as manchas: {erroManchas}</Text> : null}
        </Flex>
      ) : null}
    </div>
  );

  const mapa = (
    <div
      style={{
        // No celular a coluna não tem altura fixa: `flex: 1` daria base 0 e o mapa sumiria (medido: 2 px).
        position: 'relative', flex: celular ? '0 0 auto' : 1, minWidth: 0,
        height: celular ? 'max(420px, calc(100vh - 230px))' : undefined,
        borderRadius: 8, overflow: 'hidden', border: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <div ref={containerRef} role="application" aria-label="Mapa de prospecção" style={{ position: 'absolute', inset: 0 }} />
      <div
        style={{
          position: 'absolute', top: 10, left: 10, zIndex: 2, background: token.colorBgContainer, borderRadius: 6,
          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        }}
      >
        <Segmented<Base>
          size="small" value={base} onChange={setBase}
          options={[{ label: 'Mapa', value: 'osm' }, { label: 'Satélite', value: 'satelite' }]}
        />
      </div>
      {lendo ? <Spin style={{ position: 'absolute', top: 12, left: '50%', zIndex: 2 }} /> : null}
      {legenda}
    </div>
  );

  return (
    <>
      {erro ? <Alert type="error" showIcon closable title={erro.texto} style={{ marginBottom: 8 }} onClose={() => setErro(null)} /> : null}
      <div
        style={{
          display: 'flex', flexDirection: celular ? 'column' : 'row', gap: 12,
          height: celular ? undefined : 'calc(100vh - 170px)', minHeight: celular ? undefined : 520,
        }}
      >
      {painel}
      {mapa}
      <Drawer
        open={!!selecionado} onClose={() => setSelecionadoId(null)} size="min(560px, 94vw)" destroyOnHidden
        title={selecionado?.razao_social}
      >
        {selecionado ? (
          <Gaveta
            key={`${selecionado.id}-${selecionado.atualizado_em}`}
            p={selecionado} rotulos={rotulos} podeEditar={pode} hoje={hoje}
            onSalvo={aoSalvar} onConflito={() => carregar(dados.campanha ?? undefined)}
            onCentralizar={() => { voarPara(selecionado); setSelecionadoId(null); }}
          />
        ) : null}
      </Drawer>
      </div>
    </>
  );
}
